import styled from '@emotion/styled';
import {Spacer, spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';
import * as motion from 'motion/react-m';

import {useBlockIntro} from 'UI/components/layout/BlockIntro/useBlockIntro';
import {PayloadLexical} from 'UI/components/layout/PayloadLexical';
import {H2, Subline} from 'UI/components/layout/Text';
import {ViewportTrigger} from 'UI/components/layout/ViewportTrigger';

import {FadeInAnimation} from 'UI/animations/shared';

import {transient} from 'Application/configs/emotionTransientProps';

import type {ModuleIntro} from 'types/payload-types';

/**
 * The `ComponentProps` interface defines the properties for the `BlockIntro` component.
 * This interface specifies the configuration options for rendering an introductory section of content blocks, including optional block identification, intro content data, and testing identifiers.
 * The interface supports flexible intro content rendering by allowing null values when no intro data is available.
 */
interface ComponentProps {
    /**
     * Optional identifier for the block, used as the headline's HTML id attribute for accessibility and anchor linking.
     * When provided, this value enables users to create direct links to specific content sections and improves navigation accessibility.
     * The value can be null when no specific block identification is required.
     */
    blockId?: string | null;
    /**
     * Optional flag to control whether the component should render without bottom padding.
     * When set to true, the component will omit the default bottom padding, allowing for tighter spacing between adjacent content blocks.
     * This property is useful for creating seamless visual flow between consecutive sections or when custom spacing is handled by parent components.
     *
     * @default false
     */
    hasNoPadding?: boolean;
    /**
     * The intro module data containing headline, subline, and rich text content, or null if no intro is provided.
     * This property holds the structured content data that will be rendered in the introductory section of the block.
     * When null, the component will not render any intro content and may return null entirely if no other content is available.
     */
    intro: ModuleIntro | null;
    /**
     * The `testId` property represents a unique identifier for testing purposes.
     * This property is crucial for uniquely identifying components during testing scenarios and automated test suites.
     * It enables reliable component selection and interaction verification during both unit and end-to-end testing.
     *
     * @default 'BlockIntro'
     */
    testId?: string;
}

/**
 * The `BlockIntro` component renders an introductory section for content blocks with optional headline, subline, and rich text content.
 * This component provides a flexible way to display block introductions by conditionally rendering different text elements based on the provided intro data.
 * If no content is available (no headline, subline, or text), the component returns null to avoid rendering empty markup.
 *
 * @param props         The component props.
 * @param props.blockId Optional identifier for the block, used as the headline's HTML id attribute for accessibility and anchor linking.
 * @param props.intro   The intro module data containing headline, subline, and rich text content, or null if no intro is provided.
 * @param props.testId  Test identifier for the component used in automated testing scenarios.
 * @returns The rendered block intro JSX element or null if no content is available.
 *
 * @example
 * ```tsx
 * <BlockIntro
 *     blockId="section-1"
 *     intro={{
 *         headline: "Welcome to our services",
 *         subline: "Professional solutions",
 *         text: [{ type: 'paragraph', children: [{ text: 'Learn more about what we offer.' }] }]
 *     }}
 *     testId="service-intro"
 * />
 * ```
 */
const BlockIntro = observer(({blockId, hasNoPadding, intro, testId = 'BlockIntro'}: ComponentProps) => {
    const {headline, subline, text} = useBlockIntro(intro);

    return ((headline || subline || text) ? (
        <ViewportTrigger $hasNoPadding={hasNoPadding} baseComponent={Header} data-cy={testId}>
            {headline && <MotionH2 id={blockId ?? ''} variants={FadeInAnimation}>{headline}</MotionH2>}
            {subline && (
                <>
                    <Spacer y={2} />
                    <MotionSubline variants={FadeInAnimation}>{subline}</MotionSubline>
                </>
            )}
            {text && (
                <>
                    <Spacer y={6} />
                    <TextWrapper variants={FadeInAnimation}>
                        <PayloadLexical description={text} />
                    </TextWrapper>
                </>
            )}
        </ViewportTrigger>
    ) : null);
});

BlockIntro.displayName = 'BlockIntro';

export {BlockIntro};

interface HeaderProps {
    $hasNoPadding?: boolean;
}

const Header = styled('header', transient)<HeaderProps>`
    padding-block-end: ${({$hasNoPadding}) => ($hasNoPadding ? null : spacing(22))};
`;

const MotionH2 = H2.withComponent(motion.h2);

const MotionSubline = styled(motion.create(Subline))``;

const TextWrapper = styled(motion.div)`
    max-width: 82.3rem;
    width: 100%;
`;